import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ClientConfigService, ClientConfig } from '../services/client-config.service';
import { LeaderboardService, LeaderboardEntry } from '../services/leaderboard.service';
import { SoundService } from '../services/sound.service';
import { OnScreenKeyboardComponent } from '../components/on-screen-keyboard/on-screen-keyboard.component';

declare global {
  interface Window {
    electronAPI?: {
      exitKioskMode: (password: string) => void;
      enterKioskMode: () => void;
      closeApp: () => void;
      onExitKioskResult: (callback: (result: { success: boolean }) => void) => () => void;
      getScreenOrientation: () => string;
    };
  }
}

interface MemoryCard {
  id: number;
  icon: string;
  label: string;
  flipped: boolean;
  matched: boolean;
}

interface PuzzleTile {
  value: number;
  isEmpty: boolean;
}

type GameType = 'menu' | 'memory' | 'puzzle' | 'reaction' | 'simon' | 'whack' | 'speedtap' | 'balloonpop';

interface Balloon {
  id: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  color: string;
  points: number;
  popped: boolean;
}

interface SpeedTapTarget {
  x: number;
  y: number;
  size: number;
}

@Component({
  selector: 'app-games',
  standalone: true,
  imports: [CommonModule, FormsModule, OnScreenKeyboardComponent],
  templateUrl: './games.component.html',
  styleUrls: ['./games.component.scss']
})
export class GamesComponent implements OnInit, OnDestroy {
  activeGame: GameType = 'menu';
  showExitDialog = false;
  exitPassword = '';
  exitError = false;

  // ── Secret Menu ──
  showSecretMenu = false;
  secretPassword = '';
  secretError = false;
  secretAuthenticated = false;
  secretConfigCode = '';
  secretConfigLoading = false;
  secretConfigMessage = '';
  currentClientCode = '';

  // ── Leaderboard View ──
  showLeaderboard = false;
  leaderboardGameFilter: string = 'all';
  leaderboardEntries: LeaderboardEntry[] = [];
  soundEnabled = true;

  // ── Menu game definitions ──
  gameList = [
    { id: 'memory' as GameType, icon: '🧠', title: 'Memory Match', desc: 'Encuentra los pares ocultos', badge: '8 pares', color: '#6366f1' },
    { id: 'puzzle' as GameType, icon: '🧩', title: 'Slide Puzzle', desc: 'Ordena las fichas del 1 al 15', badge: '4×4', color: '#8b5cf6' },
    { id: 'reaction' as GameType, icon: '⚡', title: 'Reaction Time', desc: 'Pon a prueba tus reflejos', badge: 'Reflejos', color: '#f59e0b' },
    { id: 'simon' as GameType, icon: '🎵', title: 'Simon Says', desc: 'Memoriza la secuencia de colores', badge: 'Memoria', color: '#10b981' },
    { id: 'whack' as GameType, icon: '🎯', title: 'Atrapa el Logo', desc: 'Atrapa los logos antes de que desaparezcan', badge: '30 seg', color: '#ef4444' },
    { id: 'speedtap' as GameType, icon: '👆', title: 'Speed Tap', desc: 'Toca los objetivos lo más rápido posible', badge: '30 seg', color: '#0ea5e9' },
    { id: 'balloonpop' as GameType, icon: '🎈', title: 'Balloon Pop', desc: 'Revienta los globos antes de que escapen', badge: '45 seg', color: '#d946ef' },
  ];

  // ── Memory Game ──
  memoryCards: MemoryCard[] = [];
  memoryMoves = 0;
  memoryMatches = 0;
  memoryTime = 0;
  memoryBest = 0;
  memoryCompleted = false;
  private memoryTimer: any;
  private flippedCards: MemoryCard[] = [];
  private memoryLocked = false;

  private readonly cardIcons = [
    { icon: '📡', label: 'Señal' },
    { icon: '📱', label: 'Móvil' },
    { icon: '🌐', label: 'Red' },
    { icon: '⚡', label: 'Energía' },
    { icon: '🚀', label: 'Velocidad' },
    { icon: '💎', label: 'Premium' },
    { icon: '🔒', label: 'Seguridad' },
    { icon: '☁️', label: 'Cloud' },
  ];

  // ── Sliding Puzzle ──
  puzzleTiles: PuzzleTile[] = [];
  puzzleMoves = 0;
  puzzleTime = 0;
  puzzleBest = 0;
  puzzleCompleted = false;
  private puzzleTimer: any;

  // ── Reaction Game ──
  reactionState: 'idle' | 'waiting' | 'ready' | 'clicked' | 'too-early' = 'idle';
  reactionTime = 0;
  reactionBest = 0;
  reactionAttempts: number[] = [];
  private reactionTimeout: any;
  private reactionStart = 0;

  // ── Simon Says (9 colors, 3x3 grid) ──
  allSimonColors = [
    'red', 'green', 'blue', 'yellow', 'purple',
    'cyan', 'orange', 'pink', 'lime'
  ];
  simonColors: string[] = [];
  simonColorCount = 9;
  simonSpeedMs = 400;
  simonSequence: number[] = [];
  simonPlayerIndex = 0;
  simonLevel = 0;
  simonBest = 0;
  simonState: 'idle' | 'showing' | 'player' | 'gameover' = 'idle';
  simonActiveColor: number | null = null;
  simonPlayerFlash: number | null = null;
  private simonShowTimeout: any;

  // ── Whack-a-Mole ──
  whackGrid: boolean[] = Array(9).fill(false);
  whackScore = 0;
  whackBest = 0;
  whackDuration = 30;
  whackTimeLeft = 30;
  whackState: 'idle' | 'playing' | 'done' = 'idle';
  whackMissed = 0;
  private whackTimer: any;
  private whackMoleTimeout: any;

  // ── Speed Tap ──
  speedTapTarget: SpeedTapTarget | null = null;
  speedTapScore = 0;
  speedTapBest = 0;
  speedTapTimeLeft = 30;
  speedTapState: 'idle' | 'playing' | 'done' = 'idle';
  private speedTapTimer: any;
  private speedTapTimeout: any;

  // ── Balloon Pop ──
  balloons: Balloon[] = [];
  balloonScore = 0;
  balloonBest = 0;
  balloonTimeLeft = 45;
  balloonState: 'idle' | 'playing' | 'done' = 'idle';
  balloonCombo = 0;
  balloonMaxCombo = 0;
  private balloonTimer: any;
  private balloonSpawnInterval: any;
  private balloonAnimFrame: any;
  private balloonIdCounter = 0;

  Math = Math;

  // Particles
  particles: { x: number; y: number; size: number; speed: number; opacity: number }[] = [];

  // ── Client Config ──
  config!: ClientConfig;
  clientLogo = 'assets/entel-logo.png';
  clientName = 'Entel';
  clientSubtitle = 'Zona de entretenimiento';

  // ── Record / Celebration ──
  showRecordOverlay = false;
  recordStep: 'celebrate' | 'input-rut' | 'input-name' | 'saved' = 'celebrate';
  recordGameId = '';
  recordScore = 0;
  recordIsHigherBetter = true;
  recordPlayerRut = '';
  recordPlayerName = '';
  isNewRecord = false;
  confettiPieces: { x: number; y: number; color: string; rotation: number; size: number }[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private router: Router,
    private configService: ClientConfigService,
    public leaderboard: LeaderboardService,
    public sound: SoundService
  ) {}

  ngOnInit(): void {
    // Restore client code from previous session
    const savedCode = localStorage.getItem('kiosk_client_code');
    if (savedCode) {
      this.currentClientCode = savedCode;
      this.leaderboard.setClientCode(savedCode);
      this.autoFetchConfigFromApi(savedCode);
    }

    this.applyCurrentConfig();
    this.loadBestScores();
    this.initParticles();
  }

  private applyCurrentConfig(): void {
    this.config = this.configService.getConfig();
    this.clientLogo = this.config.client.logo;
    this.clientName = this.config.client.name;
    this.clientSubtitle = this.config.client.subtitle;

    this.rebuildGameList();

    // Simon: use configured number of colors
    this.simonColorCount = this.config.games.simon.colors || 9;
    this.simonColors = this.allSimonColors.slice(0, this.simonColorCount);
    this.simonSpeedMs = this.config.games.simon.speedMs || 400;

    // Whack: use configured duration
    this.whackDuration = this.config.games.whack.durationSeconds || 30;
    this.whackTimeLeft = this.whackDuration;
  }

  private autoFetchConfigFromApi(code: string): void {
    import('../../environments/environment').then(env => {
      fetch(`${env.environment.apiUrl}/clients/${code}/`)
        .then(res => {
          if (!res.ok) throw new Error('not found');
          return res.json();
        })
        .then(data => {
          this.config = this.configService.applyRemoteConfig(data);
          this.clientLogo = this.config.client.logo;
          this.clientName = this.config.client.name;
          this.clientSubtitle = this.config.client.subtitle;
          this.rebuildGameList();
          this.cdr.detectChanges();
        })
        .catch(() => {
          console.warn(`Auto-fetch config for "${code}" failed, using local config`);
        });
    });
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.memoryTimer) clearInterval(this.memoryTimer);
    if (this.puzzleTimer) clearInterval(this.puzzleTimer);
    if (this.reactionTimeout) clearTimeout(this.reactionTimeout);
    if (this.simonShowTimeout) clearTimeout(this.simonShowTimeout);
    if (this.whackTimer) clearInterval(this.whackTimer);
    if (this.whackMoleTimeout) clearTimeout(this.whackMoleTimeout);
    if (this.speedTapTimer) clearInterval(this.speedTapTimer);
    if (this.speedTapTimeout) clearTimeout(this.speedTapTimeout);
    if (this.balloonTimer) clearInterval(this.balloonTimer);
    if (this.balloonSpawnInterval) clearInterval(this.balloonSpawnInterval);
    if (this.balloonAnimFrame) cancelAnimationFrame(this.balloonAnimFrame);
  }

  // ═══════════════════════════════
  // LEADERBOARD VIEW
  // ═══════════════════════════════
  openLeaderboard(): void {
    this.sound.tap();
    this.showLeaderboard = true;
    this.leaderboardGameFilter = 'all';
    this.refreshLeaderboard();
  }

  closeLeaderboard(): void {
    this.sound.tap();
    this.showLeaderboard = false;
  }

  filterLeaderboard(gameId: string): void {
    this.sound.tap();
    this.leaderboardGameFilter = gameId;
    this.refreshLeaderboard();
  }

  private refreshLeaderboard(): void {
    if (this.leaderboardGameFilter === 'all') {
      // Get top 3 from each game, combined
      const allEntries: LeaderboardEntry[] = [];
      for (const g of this.gameList) {
        allEntries.push(...this.leaderboard.getTopScores(g.id, 3));
      }
      this.leaderboardEntries = allEntries;
    } else {
      this.leaderboardEntries = this.leaderboard.getTopScores(this.leaderboardGameFilter, 10);
    }
  }

  getGameTitle(gameId: string): string {
    const game = this.gameList.find(g => g.id === gameId);
    return game ? game.title : gameId;
  }

  getGameIcon(gameId: string): string {
    const game = this.gameList.find(g => g.id === gameId);
    return game ? game.icon : '🎮';
  }

  toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    this.sound.setEnabled(this.soundEnabled);
    if (this.soundEnabled) {
      this.sound.tap();
    }
  }

  selectGame(game: GameType): void {
    this.sound.tap();
    this.clearTimers();
    this.activeGame = game;
    if (game === 'memory') this.initMemory();
    if (game === 'puzzle') this.initPuzzle();
    if (game === 'reaction') this.resetReaction();
    if (game === 'simon') this.resetSimon();
    if (game === 'whack') this.resetWhack();
    if (game === 'speedtap') this.resetSpeedTap();
    if (game === 'balloonpop') this.resetBalloonPop();
  }

  backToMenu(): void {
    this.sound.tap();
    this.clearTimers();
    this.activeGame = 'menu';
  }

  // ── Secret Menu (replaces exit dialog) ──
  openExitDialog(): void {
    this.showSecretMenu = true;
    this.secretPassword = '';
    this.secretError = false;
    this.secretAuthenticated = false;
    this.secretConfigCode = '';
    this.secretConfigMessage = '';
  }

  closeSecretMenu(): void {
    this.showSecretMenu = false;
    this.secretPassword = '';
    this.secretError = false;
    this.secretAuthenticated = false;
    this.secretConfigCode = '';
    this.secretConfigMessage = '';
  }

  getSecretPassword(): string {
    const day = new Date().getDate().toString().padStart(2, '0');
    return '00' + day;
  }

  trySecretAuth(): void {
    if (this.secretPassword === this.getSecretPassword()) {
      this.secretAuthenticated = true;
      this.secretError = false;
    } else {
      this.secretError = true;
    }
  }

  secretExitKiosk(): void {
    const configPassword = this.config?.kiosk?.exitPassword || '1234';
    if (window.electronAPI) {
      window.electronAPI.exitKioskMode(configPassword);
      window.electronAPI.onExitKioskResult((result: { success: boolean }) => {
        if (result.success) {
          this.closeSecretMenu();
          this.cdr.detectChanges();
        }
      });
    } else {
      this.closeSecretMenu();
    }
  }

  secretGoToAdmin(): void {
    this.closeSecretMenu();
    this.router.navigate(['/admin']);
  }

  secretApplyConfig(): void {
    if (!this.secretConfigCode.trim()) return;
    this.secretConfigLoading = true;
    this.secretConfigMessage = '';

    import('../../environments/environment').then(env => {
      const url = `${env.environment.apiUrl}/clients/${this.secretConfigCode.trim()}/`;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`Cliente "${this.secretConfigCode}" no encontrado`);
          return res.json();
        })
        .then(data => {
          this.secretConfigLoading = false;
          this.currentClientCode = this.secretConfigCode.trim().toUpperCase();
          this.leaderboard.setClientCode(this.currentClientCode);
          localStorage.setItem('kiosk_client_code', this.currentClientCode);
          this.secretConfigMessage = `✅ Configuración de "${data.client.name}" aplicada`;
          // Apply the config to the kiosk
          this.config = this.configService.applyRemoteConfig(data);
          this.clientLogo = this.config.client.logo;
          this.clientName = this.config.client.name;
          this.clientSubtitle = this.config.client.subtitle;
          // Rebuild game list
          this.rebuildGameList();
          this.cdr.detectChanges();
        })
        .catch(err => {
          this.secretConfigLoading = false;
          this.secretConfigMessage = `❌ ${err.message}`;
          this.cdr.detectChanges();
        });
    });
  }

  secretRefreshConfig(): void {
    if (!this.currentClientCode) {
      this.secretConfigMessage = '⚠️ No hay código de cliente configurado. Ingresa uno primero.';
      return;
    }
    this.secretConfigLoading = true;
    this.secretConfigMessage = '';

    import('../../environments/environment').then(env => {
      const url = `${env.environment.apiUrl}/clients/${this.currentClientCode}/`;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error(`Cliente "${this.currentClientCode}" no encontrado`);
          return res.json();
        })
        .then(data => {
          this.secretConfigLoading = false;
          this.secretConfigMessage = `✅ Configuración actualizada desde "${data.client.name}"`;
          this.config = this.configService.applyRemoteConfig(data);
          this.clientLogo = this.config.client.logo;
          this.clientName = this.config.client.name;
          this.clientSubtitle = this.config.client.subtitle;
          this.rebuildGameList();
          this.cdr.detectChanges();
        })
        .catch(err => {
          this.secretConfigLoading = false;
          this.secretConfigMessage = `❌ ${err.message}`;
          this.cdr.detectChanges();
        });
    });
  }

  private rebuildGameList(): void {
    const allGames = [
      { id: 'memory' as GameType, icon: '🧠', title: 'Memory Match', desc: 'Encuentra los pares ocultos', badge: '8 pares', color: '#6366f1' },
      { id: 'puzzle' as GameType, icon: '🧩', title: 'Slide Puzzle', desc: 'Ordena las fichas del 1 al 15', badge: '4×4', color: '#8b5cf6' },
      { id: 'reaction' as GameType, icon: '⚡', title: 'Reaction Time', desc: 'Pon a prueba tus reflejos', badge: 'Reflejos', color: '#f59e0b' },
      { id: 'simon' as GameType, icon: '🎵', title: 'Simon Says', desc: 'Memoriza la secuencia de colores', badge: 'Memoria', color: '#10b981' },
      { id: 'whack' as GameType, icon: '🎯', title: 'Atrapa el Logo', desc: 'Atrapa los logos antes de que desaparezcan', badge: '30 seg', color: '#ef4444' },
      { id: 'speedtap' as GameType, icon: '👆', title: 'Speed Tap', desc: 'Toca los objetivos lo más rápido posible', badge: '30 seg', color: '#0ea5e9' },
      { id: 'balloonpop' as GameType, icon: '🎈', title: 'Balloon Pop', desc: 'Revienta los globos antes de que escapen', badge: '45 seg', color: '#d946ef' },
    ];
    this.gameList = allGames.filter(g => {
      const gameConf = this.config.games[g.id as keyof ClientConfig['games']];
      return gameConf && gameConf.enabled;
    });
  }

  closeExitDialog(): void {
    this.closeSecretMenu();
  }

  closeApp(): void {
    if (window.electronAPI) {
      window.electronAPI.closeApp();
    }
  }

  // ═══════════════════════════════
  // MEMORY GAME
  // ═══════════════════════════════
  initMemory(): void {
    if (this.memoryTimer) clearInterval(this.memoryTimer);
    this.memoryMoves = 0;
    this.memoryMatches = 0;
    this.memoryTime = 0;
    this.memoryCompleted = false;
    this.flippedCards = [];
    this.memoryLocked = false;

    const pairs = [...this.cardIcons];
    const doubled = [...pairs, ...pairs];
    this.memoryCards = this.shuffle(doubled).map((item, i) => ({
      id: i,
      icon: item.icon,
      label: item.label,
      flipped: false,
      matched: false
    }));

    this.memoryTimer = setInterval(() => this.memoryTime++, 1000);
  }

  flipCard(card: MemoryCard): void {
    if (this.memoryLocked || card.flipped || card.matched) return;

    card.flipped = true;
    this.sound.flip();
    this.flippedCards.push(card);

    if (this.flippedCards.length === 2) {
      this.memoryMoves++;
      this.memoryLocked = true;

      if (this.flippedCards[0].icon === this.flippedCards[1].icon) {
        this.sound.hit();
        this.flippedCards[0].matched = true;
        this.flippedCards[1].matched = true;
        this.memoryMatches++;
        this.flippedCards = [];
        this.memoryLocked = false;

        if (this.memoryMatches === this.cardIcons.length) {
          clearInterval(this.memoryTimer);
          this.memoryCompleted = true;
          this.sound.victory();
          if (!this.memoryBest || this.memoryTime < this.memoryBest) {
            this.memoryBest = this.memoryTime;
            localStorage.setItem('kiosk_memory_best', this.memoryTime.toString());
          }
          this.checkMemoryRecord();
        }
      } else {
        this.sound.miss();
        setTimeout(() => {
          this.flippedCards[0].flipped = false;
          this.flippedCards[1].flipped = false;
          this.flippedCards = [];
          this.memoryLocked = false;
        }, 800);
      }
    }
  }

  // ═══════════════════════════════
  // SLIDING PUZZLE
  // ═══════════════════════════════
  initPuzzle(): void {
    if (this.puzzleTimer) clearInterval(this.puzzleTimer);
    this.puzzleMoves = 0;
    this.puzzleTime = 0;
    this.puzzleCompleted = false;

    let tiles: number[];
    do {
      tiles = this.shuffle([...Array(15).keys()].map(i => i + 1));
      tiles.push(0);
    } while (!this.isSolvable(tiles));

    this.puzzleTiles = tiles.map(v => ({ value: v, isEmpty: v === 0 }));
    this.puzzleTimer = setInterval(() => this.puzzleTime++, 1000);
  }

  moveTile(index: number): void {
    if (this.puzzleCompleted) return;
    const emptyIdx = this.puzzleTiles.findIndex(t => t.isEmpty);
    const row = Math.floor(index / 4);
    const col = index % 4;
    const emptyRow = Math.floor(emptyIdx / 4);
    const emptyCol = emptyIdx % 4;

    const isAdjacent =
      (Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (Math.abs(col - emptyCol) === 1 && row === emptyRow);

    if (isAdjacent) {
      this.sound.slide();
      [this.puzzleTiles[index], this.puzzleTiles[emptyIdx]] =
        [this.puzzleTiles[emptyIdx], this.puzzleTiles[index]];
      this.puzzleMoves++;

      if (this.checkPuzzleSolved()) {
        clearInterval(this.puzzleTimer);
        this.puzzleCompleted = true;
        this.sound.victory();
        if (!this.puzzleBest || this.puzzleMoves < this.puzzleBest) {
          this.puzzleBest = this.puzzleMoves;
          localStorage.setItem('kiosk_puzzle_best', this.puzzleMoves.toString());
        }
        this.checkPuzzleRecord();
      }
    }
  }

  private checkPuzzleSolved(): boolean {
    for (let i = 0; i < 15; i++) {
      if (this.puzzleTiles[i].value !== i + 1) return false;
    }
    return this.puzzleTiles[15].isEmpty;
  }

  private isSolvable(tiles: number[]): boolean {
    let inv = 0;
    for (let i = 0; i < tiles.length; i++) {
      for (let j = i + 1; j < tiles.length; j++) {
        if (tiles[i] && tiles[j] && tiles[i] > tiles[j]) inv++;
      }
    }
    const emptyRow = Math.floor(tiles.indexOf(0) / 4);
    return (emptyRow % 2 === 0) ? (inv % 2 !== 0) : (inv % 2 === 0);
  }

  // ═══════════════════════════════
  // REACTION GAME
  // ═══════════════════════════════
  resetReaction(): void {
    this.reactionState = 'idle';
    this.reactionTime = 0;
    this.reactionAttempts = [];
    if (this.reactionTimeout) clearTimeout(this.reactionTimeout);
  }

  startReaction(): void {
    this.reactionState = 'waiting';
    this.cdr.detectChanges();
    const delay = 800 + Math.random() * 2500;
    this.reactionTimeout = setTimeout(() => {
      this.ngZone.run(() => {
        this.reactionState = 'ready';
        this.reactionStart = performance.now();
        this.sound.go();
        this.cdr.detectChanges();
      });
    }, delay);
  }

  clickReaction(): void {
    if (this.reactionState === 'waiting') {
      clearTimeout(this.reactionTimeout);
      this.reactionState = 'too-early';
      this.sound.miss();
    } else if (this.reactionState === 'ready') {
      this.reactionTime = Math.round(performance.now() - this.reactionStart);
      this.reactionAttempts.push(this.reactionTime);
      this.reactionState = 'clicked';
      this.sound.hit();
      if (!this.reactionBest || this.reactionTime < this.reactionBest) {
        this.reactionBest = this.reactionTime;
        localStorage.setItem('kiosk_reaction_best', this.reactionTime.toString());
      }
      this.checkReactionRecord();
    }
  }

  getReactionAverage(): number {
    if (!this.reactionAttempts.length) return 0;
    return Math.round(this.reactionAttempts.reduce((a, b) => a + b, 0) / this.reactionAttempts.length);
  }

  // ═══════════════════════════════
  // SIMON SAYS
  // ═══════════════════════════════
  resetSimon(): void {
    this.simonSequence = [];
    this.simonLevel = 0;
    this.simonState = 'idle';
    this.simonActiveColor = null;
    this.simonPlayerFlash = null;
    this.simonPlayerIndex = 0;
    if (this.simonShowTimeout) clearTimeout(this.simonShowTimeout);
  }

  startSimon(): void {
    this.simonSequence = [];
    this.simonLevel = 0;
    this.simonState = 'showing';
    this.simonAddAndShow();
  }

  private simonAddAndShow(): void {
    this.simonSequence.push(Math.floor(Math.random() * this.simonColorCount));
    this.simonLevel = this.simonSequence.length;
    this.simonState = 'showing';
    this.simonActiveColor = null;
    this.simonPlayerIndex = 0;

    // Speed increases as levels go up
    const showDuration = Math.max(200, this.simonSpeedMs - this.simonLevel * 15);
    const gapDuration = Math.max(100, 180 - this.simonLevel * 8);

    let i = 0;
    const showNext = () => {
      if (i < this.simonSequence.length) {
        this.simonActiveColor = this.simonSequence[i];
        this.sound.simonTone(this.simonSequence[i]);
        this.cdr.detectChanges();
        this.simonShowTimeout = setTimeout(() => {
          this.simonActiveColor = null;
          this.cdr.detectChanges();
          i++;
          this.simonShowTimeout = setTimeout(() => showNext(), gapDuration);
        }, showDuration);
      } else {
        this.simonState = 'player';
        this.cdr.detectChanges();
      }
    };
    setTimeout(() => showNext(), 400);
  }

  simonPress(colorIndex: number): void {
    if (this.simonState !== 'player') return;

    this.simonPlayerFlash = colorIndex;
    this.sound.simonTone(colorIndex);
    setTimeout(() => {
      this.simonPlayerFlash = null;
      this.cdr.detectChanges();
    }, 200);

    if (colorIndex === this.simonSequence[this.simonPlayerIndex]) {
      this.simonPlayerIndex++;
      if (this.simonPlayerIndex === this.simonSequence.length) {
        this.sound.score();
        if (this.simonLevel > this.simonBest) {
          this.simonBest = this.simonLevel;
          localStorage.setItem('kiosk_simon_best', this.simonLevel.toString());
        }
        setTimeout(() => this.simonAddAndShow(), 800);
      }
    } else {
      this.simonState = 'gameover';
      this.sound.gameOver();
      if (this.simonLevel > this.simonBest) {
        this.simonBest = this.simonLevel;
        localStorage.setItem('kiosk_simon_best', this.simonLevel.toString());
      }
      this.checkSimonRecord();
    }
  }

  // ═══════════════════════════════
  // WHACK-A-MOLE
  // ═══════════════════════════════
  resetWhack(): void {
    this.whackGrid = Array(9).fill(false);
    this.whackScore = 0;
    this.whackTimeLeft = this.whackDuration;
    this.whackState = 'idle';
    this.whackMissed = 0;
    if (this.whackTimer) clearInterval(this.whackTimer);
    if (this.whackMoleTimeout) clearTimeout(this.whackMoleTimeout);
  }

  startWhack(): void {
    this.resetWhack();
    this.whackState = 'playing';
    this.whackTimer = setInterval(() => {
      this.whackTimeLeft--;
      if (this.whackTimeLeft <= 5 && this.whackTimeLeft > 0) {
        this.sound.warning();
      }
      if (this.whackTimeLeft <= 0) {
        this.endWhack();
      }
    }, 1000);
    this.spawnMole();
  }

  private spawnMole(): void {
    if (this.whackState !== 'playing') return;
    this.whackGrid = Array(9).fill(false);
    const count = this.whackScore > 15 ? 2 : 1;
    const indices = new Set<number>();
    while (indices.size < count) {
      indices.add(Math.floor(Math.random() * 9));
    }
    indices.forEach(idx => this.whackGrid[idx] = true);
    this.cdr.detectChanges();

    const hideDelay = Math.max(500, 1200 - this.whackScore * 20);
    this.whackMoleTimeout = setTimeout(() => {
      const activeMoles = this.whackGrid.filter(v => v).length;
      this.whackMissed += activeMoles;
      this.whackGrid = Array(9).fill(false);
      this.cdr.detectChanges();
      this.whackMoleTimeout = setTimeout(() => this.spawnMole(), 300);
    }, hideDelay);
  }

  whackHit(index: number): void {
    if (this.whackState !== 'playing' || !this.whackGrid[index]) return;
    this.whackGrid = this.whackGrid.map((v, i) => i === index ? false : v);
    this.whackScore++;
    this.sound.hit();
    this.cdr.detectChanges();

    // If all moles hit, skip the hide timeout and spawn next immediately
    if (!this.whackGrid.some(v => v)) {
      if (this.whackMoleTimeout) clearTimeout(this.whackMoleTimeout);
      this.whackMoleTimeout = setTimeout(() => this.spawnMole(), 200);
    }
  }

  private endWhack(): void {
    this.whackState = 'done';
    this.whackGrid = Array(9).fill(false);
    if (this.whackTimer) clearInterval(this.whackTimer);
    if (this.whackMoleTimeout) clearTimeout(this.whackMoleTimeout);
    this.sound.gameOver();
    if (!this.whackBest || this.whackScore > this.whackBest) {
      this.whackBest = this.whackScore;
      localStorage.setItem('kiosk_whack_best', this.whackScore.toString());
    }
    this.checkWhackRecord();
  }

  // ═══════════════════════════════
  // UTILITIES
  // ═══════════════════════════════
  private shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  private initParticles(): void {
    this.particles = Array.from({ length: 30 }, () => ({
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 4,
      speed: 0.2 + Math.random() * 0.5,
      opacity: 0.1 + Math.random() * 0.3
    }));
  }

  getParticleStyle(p: { x: number; y: number; size: number; speed: number; opacity: number }): { [key: string]: string } {
    return {
      left: p.x + '%',
      top: p.y + '%',
      width: p.size + 'px',
      height: p.size + 'px',
      opacity: p.opacity.toString(),
      'animation-duration': (10 / p.speed) + 's'
    };
  }

  getBestForGame(gameId: string): string {
    switch (gameId) {
      case 'memory': return this.memoryBest ? this.formatTime(this.memoryBest) : '';
      case 'puzzle': return this.puzzleBest ? `${this.puzzleBest} movs` : '';
      case 'reaction': return this.reactionBest ? `${this.reactionBest}ms` : '';
      case 'simon': return this.simonBest ? `Nivel ${this.simonBest}` : '';
      case 'whack': return this.whackBest ? `${this.whackBest} pts` : '';
      case 'speedtap': return this.speedTapBest ? `${this.speedTapBest} pts` : '';
      case 'balloonpop': return this.balloonBest ? `${this.balloonBest} pts` : '';
      default: return '';
    }
  }

  private loadBestScores(): void {
    this.memoryBest = parseInt(localStorage.getItem('kiosk_memory_best') || '0', 10);
    this.puzzleBest = parseInt(localStorage.getItem('kiosk_puzzle_best') || '0', 10);
    this.reactionBest = parseInt(localStorage.getItem('kiosk_reaction_best') || '0', 10);
    this.simonBest = parseInt(localStorage.getItem('kiosk_simon_best') || '0', 10);
    this.whackBest = parseInt(localStorage.getItem('kiosk_whack_best') || '0', 10);
    this.speedTapBest = parseInt(localStorage.getItem('kiosk_speedtap_best') || '0', 10);
    this.balloonBest = parseInt(localStorage.getItem('kiosk_balloonpop_best') || '0', 10);
  }

  // ═══════════════════════════════
  // SPEED TAP GAME
  // ═══════════════════════════════
  resetSpeedTap(): void {
    this.speedTapTarget = null;
    this.speedTapScore = 0;
    this.speedTapTimeLeft = 30;
    this.speedTapState = 'idle';
    if (this.speedTapTimer) clearInterval(this.speedTapTimer);
    if (this.speedTapTimeout) clearTimeout(this.speedTapTimeout);
  }

  startSpeedTap(): void {
    this.resetSpeedTap();
    this.speedTapState = 'playing';
    this.speedTapTimer = setInterval(() => {
      this.speedTapTimeLeft--;
      if (this.speedTapTimeLeft <= 5 && this.speedTapTimeLeft > 0) {
        this.sound.warning();
      }
      if (this.speedTapTimeLeft <= 0) {
        this.endSpeedTap();
      }
    }, 1000);
    this.spawnSpeedTapTarget();
  }

  private spawnSpeedTapTarget(): void {
    if (this.speedTapState !== 'playing') return;
    const size = Math.max(50, 80 - this.speedTapScore);
    const x = 10 + Math.random() * 70;
    const y = 10 + Math.random() * 70;
    this.speedTapTarget = { x, y, size };
    this.cdr.detectChanges();

    // Auto-miss after a timeout (gets shorter over time)
    const missTimeout = Math.max(800, 2000 - this.speedTapScore * 30);
    this.speedTapTimeout = setTimeout(() => {
      if (this.speedTapState === 'playing') {
        this.spawnSpeedTapTarget();
      }
    }, missTimeout);
  }

  hitSpeedTap(): void {
    if (this.speedTapState !== 'playing') return;
    if (this.speedTapTimeout) clearTimeout(this.speedTapTimeout);
    this.speedTapScore++;
    this.sound.score();
    this.spawnSpeedTapTarget();
  }

  private endSpeedTap(): void {
    this.speedTapState = 'done';
    this.speedTapTarget = null;
    if (this.speedTapTimer) clearInterval(this.speedTapTimer);
    if (this.speedTapTimeout) clearTimeout(this.speedTapTimeout);
    this.sound.gameOver();
    if (!this.speedTapBest || this.speedTapScore > this.speedTapBest) {
      this.speedTapBest = this.speedTapScore;
      localStorage.setItem('kiosk_speedtap_best', this.speedTapScore.toString());
    }
    this.checkAndShowRecord('speedtap', this.speedTapScore, true);
  }

  // ═══════════════════════════════
  // BALLOON POP GAME
  // ═══════════════════════════════
  private readonly balloonColors = [
    { color: '#ef4444', points: 1 },
    { color: '#f59e0b', points: 1 },
    { color: '#10b981', points: 2 },
    { color: '#6366f1', points: 2 },
    { color: '#ec4899', points: 3 },
    { color: '#1400FF', points: 5 },
  ];

  resetBalloonPop(): void {
    this.balloons = [];
    this.balloonScore = 0;
    this.balloonTimeLeft = 45;
    this.balloonState = 'idle';
    this.balloonCombo = 0;
    this.balloonMaxCombo = 0;
    this.balloonIdCounter = 0;
    if (this.balloonTimer) clearInterval(this.balloonTimer);
    if (this.balloonSpawnInterval) clearInterval(this.balloonSpawnInterval);
    if (this.balloonAnimFrame) cancelAnimationFrame(this.balloonAnimFrame);
  }

  startBalloonPop(): void {
    this.resetBalloonPop();
    this.balloonState = 'playing';

    this.balloonTimer = setInterval(() => {
      this.balloonTimeLeft--;
      if (this.balloonTimeLeft <= 5 && this.balloonTimeLeft > 0) {
        this.sound.warning();
      }
      if (this.balloonTimeLeft <= 0) {
        this.endBalloonPop();
      }
      this.cdr.detectChanges();
    }, 1000);

    this.balloonSpawnInterval = setInterval(() => {
      if (this.balloonState === 'playing') {
        this.spawnBalloon();
      }
    }, 600);

    this.animateBalloons();
  }

  private spawnBalloon(): void {
    const colorData = this.balloonColors[Math.floor(Math.random() * this.balloonColors.length)];
    const size = 40 + Math.random() * 30;
    const balloon: Balloon = {
      id: this.balloonIdCounter++,
      x: 5 + Math.random() * 85,
      y: 105,
      size,
      speed: 0.3 + Math.random() * 0.6,
      color: colorData.color,
      points: colorData.points,
      popped: false,
    };
    this.balloons.push(balloon);
  }

  private animateBalloons(): void {
    if (this.balloonState !== 'playing') return;

    this.balloons = this.balloons
      .filter(b => !b.popped && b.y > -15)
      .map(b => ({ ...b, y: b.y - b.speed }));

    this.cdr.detectChanges();
    this.balloonAnimFrame = requestAnimationFrame(() => this.animateBalloons());
  }

  popBalloon(balloon: Balloon): void {
    if (this.balloonState !== 'playing' || balloon.popped) return;
    balloon.popped = true;
    this.sound.pop();
    this.balloonCombo++;
    if (this.balloonCombo > this.balloonMaxCombo) {
      this.balloonMaxCombo = this.balloonCombo;
    }
    const comboMultiplier = this.balloonCombo >= 5 ? 3 : this.balloonCombo >= 3 ? 2 : 1;
    this.balloonScore += balloon.points * comboMultiplier;
    if (comboMultiplier > 1) {
      this.sound.combo();
    }

    // Reset combo after a delay if no more pops
    setTimeout(() => {
      this.balloonCombo = Math.max(0, this.balloonCombo - 1);
    }, 1200);
  }

  private endBalloonPop(): void {
    this.balloonState = 'done';
    if (this.balloonTimer) clearInterval(this.balloonTimer);
    if (this.balloonSpawnInterval) clearInterval(this.balloonSpawnInterval);
    if (this.balloonAnimFrame) cancelAnimationFrame(this.balloonAnimFrame);
    this.sound.gameOver();
    if (!this.balloonBest || this.balloonScore > this.balloonBest) {
      this.balloonBest = this.balloonScore;
      localStorage.setItem('kiosk_balloonpop_best', this.balloonScore.toString());
    }
    this.checkAndShowRecord('balloonpop', this.balloonScore, true);
  }

  trackBalloon(index: number, b: Balloon): number {
    return b.id;
  }

  getBalloonStyle(b: Balloon): { [key: string]: string } {
    return {
      left: b.x + '%',
      bottom: (100 - b.y) + '%',
      width: b.size + 'px',
      height: (b.size * 1.2) + 'px',
      background: b.color,
    };
  }

  // ═══════════════════════════════
  // RECORD / CELEBRATION SYSTEM
  // ═══════════════════════════════
  checkAndShowRecord(gameId: string, score: number, isHigherBetter: boolean): void {
    const isRecord = this.leaderboard.isNewRecord({ gameId, score, isHigherBetter });
    const isTop = this.leaderboard.isTopScore({ gameId, score, isHigherBetter });

    if (isRecord || isTop) {
      this.recordGameId = gameId;
      this.recordScore = score;
      this.recordIsHigherBetter = isHigherBetter;
      this.isNewRecord = isRecord;
      this.recordPlayerRut = '';
      this.recordPlayerName = '';
      this.recordStep = 'celebrate';
      this.showRecordOverlay = true;
      this.generateConfetti();
      this.sound.record();
      this.cdr.detectChanges();

      // Auto-advance from celebration to input after 2.5s
      setTimeout(() => {
        if (this.showRecordOverlay && this.recordStep === 'celebrate') {
          this.recordStep = 'input-rut';
          this.cdr.detectChanges();
        }
      }, 2500);
    }
  }

  onRecordRutEnter(): void {
    if (this.recordPlayerRut.replace(/[^0-9kK]/g, '').length >= 8) {
      this.recordStep = 'input-name';
      this.cdr.detectChanges();
    }
  }

  onRecordNameEnter(): void {
    if (this.recordPlayerName.trim().length >= 2) {
      this.saveRecord();
    }
  }

  saveRecord(): void {
    this.leaderboard.addEntry({
      playerName: this.recordPlayerName.trim(),
      playerRut: this.recordPlayerRut,
      score: this.recordScore,
      gameId: this.recordGameId,
      date: new Date().toISOString(),
      isHigherBetter: this.recordIsHigherBetter,
    });
    this.recordStep = 'saved';
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showRecordOverlay = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  skipRecord(): void {
    this.showRecordOverlay = false;
  }

  private generateConfetti(): void {
    const colors = ['#ef4444', '#f59e0b', '#10b981', '#6366f1', '#ec4899', '#1400FF', '#0ea5e9'];
    this.confettiPieces = Array.from({ length: 60 }, () => ({
      x: Math.random() * 100,
      y: -10 - Math.random() * 40,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      size: 6 + Math.random() * 10,
    }));
  }

  // Hook into existing game completions for record checking
  private checkMemoryRecord(): void {
    this.checkAndShowRecord('memory', this.memoryTime, false);
  }

  private checkPuzzleRecord(): void {
    this.checkAndShowRecord('puzzle', this.puzzleMoves, false);
  }

  private checkReactionRecord(): void {
    this.checkAndShowRecord('reaction', this.reactionTime, false);
  }

  private checkSimonRecord(): void {
    this.checkAndShowRecord('simon', this.simonLevel, true);
  }

  private checkWhackRecord(): void {
    this.checkAndShowRecord('whack', this.whackScore, true);
  }
}
